import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SyncIcon from "@mui/icons-material/Sync";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import axios from "../axiosConfig";

// A proposal row is "actionable" iff it has a non-zero, non-fractional-only
// share delta and a positive price. Mirrors backend execution.build_order_intents.
const isActionable = (p) => {
  if (!p) return false;
  if ((p.action || "").toUpperCase() === "HOLD") return false;
  if (p.skipped_reason) return false;
  const d = Number(p.delta_shares);
  if (!d || isNaN(d)) return false;
  if (Math.floor(Math.abs(d)) < 1) return false;
  const px = Number(p.price);
  if (!px || isNaN(px) || px <= 0) return false;
  return true;
};

const fmtUsd = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(v));
};

const fmtPct = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
};

const fmtNum = (v, d = 4) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return Number(v).toFixed(d);
};

const decisionColor = (d) => {
  const x = (d || "").toUpperCase();
  if (x === "BUY" || x === "OUTPERFORM") return "success";
  if (x === "SELL" || x === "UNDERPERFORM") return "error";
  if (x === "HOLD") return "default";
  return "info";
};

function TradingTab() {
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersStatus, setOrdersStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sourceType, setSourceType] = useState("watchlist");
  const [watchlists, setWatchlists] = useState([]);
  const [watchlistId, setWatchlistId] = useState("");
  const [analysisIdsInput, setAnalysisIdsInput] = useState("");
  const [tickersInput, setTickersInput] = useState("");

  const [mode, setMode] = useState("equal_weight");
  const [maxPosSize, setMaxPosSize] = useState(0.2);
  const [grossTarget, setGrossTarget] = useState(1.0);

  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [proposalsResp, setProposalsResp] = useState(null);
  const [proposalsError, setProposalsError] = useState(null);
  const [runLabel, setRunLabel] = useState("");

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);

  const [editRun, setEditRun] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Execution state (Task #10)
  const [selected, setSelected] = useState(() => new Set());
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [execDialogOpen, setExecDialogOpen] = useState(false);
  const [execDryRun, setExecDryRun] = useState(true);
  const [execConfirmInput, setExecConfirmInput] = useState("");
  const [execMaxPerOrder, setExecMaxPerOrder] = useState(10000);
  const [execMaxTotal, setExecMaxTotal] = useState(50000);
  const [execSubmitting, setExecSubmitting] = useState(false);
  const [execError, setExecError] = useState(null);
  const [execResult, setExecResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const resp = await axios.get("/api/broker/status");
      setStatus(resp.data?.data || null);
      return resp.data?.data;
    } catch (e) {
      setStatus({ configured: false, reachable: false, error: e?.message });
      return null;
    }
  }, []);

  const loadAccountAndPositions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acct, pos] = await Promise.all([
        axios.get("/api/broker/account").catch((e) => ({ error: e })),
        axios.get("/api/broker/positions").catch((e) => ({ error: e })),
      ]);
      if (acct?.data?.success) setAccount(acct.data.data);
      else setAccount(null);
      if (pos?.data?.success) setPositions(pos.data.data || []);
      else setPositions([]);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async (statusFilter) => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const resp = await axios.get("/api/broker/orders", {
        params: { status: statusFilter || "all", limit: 50 },
      });
      if (resp.data?.success) {
        setOrders(resp.data.data || []);
      } else {
        setOrdersError(resp.data?.error || "Failed to load orders");
        setOrders([]);
      }
    } catch (e) {
      setOrdersError(e?.response?.data?.error || e.message);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const loadWatchlists = useCallback(async () => {
    try {
      const resp = await axios.get("/api/watchlists");
      if (resp.data?.success) setWatchlists(resp.data.data || []);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    (async () => {
      const s = await loadStatus();
      if (s?.configured && s?.reachable) {
        await loadAccountAndPositions();
        loadOrders(ordersStatus);
      }
      loadWatchlists();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatus, loadAccountAndPositions, loadOrders, loadWatchlists]);

  const handleRefresh = async () => {
    const s = await loadStatus();
    if (s?.configured && s?.reachable) {
      await loadAccountAndPositions();
      loadOrders(ordersStatus);
    }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const resp = await axios.get("/api/broker/proposal-runs", {
        params: { limit: 50 },
      });
      if (resp.data?.success) {
        setHistory(resp.data.data || []);
      } else {
        setHistoryError(resp.data?.error || "Failed to load history");
      }
    } catch (e) {
      setHistoryError(e?.response?.data?.error || e.message);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleComputeProposals = async () => {
    setProposalsLoading(true);
    setProposalsError(null);
    setProposalsResp(null);
    setActiveRunId(null);
    try {
      const body = {
        mode,
        max_position_size: Number(maxPosSize),
        gross_target: Number(grossTarget),
        source_type: sourceType,
      };
      if (runLabel.trim()) body.label = runLabel.trim();
      if (sourceType === "watchlist" && watchlistId) {
        body.watchlist_id = Number(watchlistId);
      } else if (sourceType === "ids") {
        const ids = analysisIdsInput
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => Number(s))
          .filter((n) => !isNaN(n));
        body.analysis_ids = ids;
      } else if (sourceType === "ticker") {
        const tks = tickersInput
          .split(/[\s,]+/)
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
        body.tickers = tks;
      }
      const resp = await axios.post("/api/broker/proposals", body);
      if (resp.data?.success) {
        setProposalsResp(resp.data.data);
        setSelected(new Set());
        setExecResult(null);
        if (resp.data.data?.run_id) {
          setActiveRunId(resp.data.data.run_id);
          loadExecutions(resp.data.data.run_id);
        } else {
          setExecutions([]);
        }
        loadHistory();
      } else {
        setProposalsError(resp.data?.error || "Failed to compute proposals");
      }
    } catch (e) {
      setProposalsError(e?.response?.data?.error || e.message);
    } finally {
      setProposalsLoading(false);
    }
  };

  const handleOpenRun = async (runId) => {
    setProposalsLoading(true);
    setProposalsError(null);
    try {
      const resp = await axios.get(`/api/broker/proposal-runs/${runId}`);
      if (resp.data?.success) {
        const r = resp.data.data;
        setProposalsResp({
          mode: r.mode,
          max_position_size: r.max_position_size,
          gross_target: r.gross_target,
          equity: r.equity,
          broker_configured: r.broker_configured,
          proposals: r.proposals || [],
          warnings: r.warnings || [],
          preview_only: true,
          source_type: r.source_type,
          source_payload: r.source_payload,
          signals: r.signals || [],
          label: r.label,
          run_id: r.id,
          created_at: r.created_at,
          historical: true,
        });
        setActiveRunId(runId);
        setSelected(new Set());
        setExecResult(null);
        loadExecutions(runId);
      } else {
        setProposalsError(resp.data?.error || "Failed to load run");
      }
    } catch (e) {
      setProposalsError(e?.response?.data?.error || e.message);
    } finally {
      setProposalsLoading(false);
    }
  };

  const loadExecutions = async (runId) => {
    if (!runId) {
      setExecutions([]);
      return;
    }
    setExecutionsLoading(true);
    try {
      const resp = await axios.get(`/api/broker/proposal-runs/${runId}/executions`);
      if (resp.data?.success) {
        setExecutions(resp.data.data || []);
      }
    } catch (e) {
      // non-fatal
      setExecutions([]);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const toggleSelected = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAllActionable = () => {
    if (!proposalsResp?.proposals) return;
    const next = new Set();
    proposalsResp.proposals.forEach((p, i) => {
      if (isActionable(p)) next.add(i);
    });
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const selectionSummary = (() => {
    const proposals = proposalsResp?.proposals || [];
    const list = Array.from(selected).map((i) => proposals[i]).filter(isActionable);
    let buys = 0, sells = 0, gross = 0, net = 0;
    list.forEach((p) => {
      const qty = Math.floor(Math.abs(Number(p.delta_shares)));
      const notional = qty * Number(p.price);
      if (Number(p.delta_shares) > 0) { buys++; net += notional; }
      else { sells++; net -= notional; }
      gross += notional;
    });
    return { count: list.length, buys, sells, gross, net };
  })();

  const openExecuteDialog = () => {
    setExecError(null);
    setExecResult(null);
    setExecConfirmInput("");
    setExecDryRun(true);
    setExecDialogOpen(true);
  };

  const closeExecuteDialog = () => {
    if (execSubmitting) return;
    setExecDialogOpen(false);
  };

  const submitExecution = async () => {
    if (!proposalsResp?.run_id) return;
    if (selected.size === 0) {
      setExecError("Select at least one trade.");
      return;
    }
    if (!execDryRun && String(execConfirmInput).trim() !== String(proposalsResp.run_id)) {
      setExecError(`Type the run id (${proposalsResp.run_id}) to confirm live submission.`);
      return;
    }
    setExecSubmitting(true);
    setExecError(null);
    setExecResult(null);
    try {
      const body = {
        selected_indices: Array.from(selected),
        dry_run: execDryRun,
        max_notional_per_order: Number(execMaxPerOrder),
        max_total_notional: Number(execMaxTotal),
      };
      if (!execDryRun) body.confirm = proposalsResp.run_id;
      const resp = await axios.post(
        `/api/broker/proposal-runs/${proposalsResp.run_id}/execute`,
        body,
      );
      if (resp.data?.success) {
        setExecResult(resp.data.data);
        loadExecutions(proposalsResp.run_id);
        if (!execDryRun) {
          // Refresh account + positions + open orders so anything just submitted shows up.
          loadAccountAndPositions();
          loadOrders(ordersStatus);
        }
      } else {
        setExecError(resp.data?.error || "Execution failed");
      }
    } catch (e) {
      setExecError(e?.response?.data?.error || e.message);
    } finally {
      setExecSubmitting(false);
    }
  };

  const refreshOrder = async (orderId) => {
    try {
      const resp = await axios.post(`/api/broker/orders/${orderId}/refresh`);
      if (resp.data?.success && proposalsResp?.run_id) {
        loadExecutions(proposalsResp.run_id);
      }
    } catch (e) {
      // ignore
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!window.confirm(`Delete proposal run #${runId}?`)) return;
    try {
      await axios.delete(`/api/broker/proposal-runs/${runId}`);
      if (activeRunId === runId) {
        setProposalsResp(null);
        setActiveRunId(null);
      }
      loadHistory();
    } catch (e) {
      setHistoryError(e?.response?.data?.error || e.message);
    }
  };

  const handleOpenEdit = (run) => {
    setEditRun(run);
    setEditLabel(run?.label || "");
    setEditNotes(run?.notes || "");
    setEditError(null);
  };

  const handleCloseEdit = () => {
    if (editSaving) return;
    setEditRun(null);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editRun) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const resp = await axios.patch(
        `/api/broker/proposal-runs/${editRun.id}`,
        { label: editLabel, notes: editNotes },
      );
      if (resp.data?.success) {
        const updated = resp.data.data;
        setHistory((prev) =>
          prev.map((h) =>
            h.id === updated.id
              ? { ...h, label: updated.label, notes: updated.notes }
              : h,
          ),
        );
        if (activeRunId === updated.id && proposalsResp) {
          setProposalsResp({
            ...proposalsResp,
            label: updated.label,
            notes: updated.notes,
          });
        }
        setEditRun(null);
      } else {
        setEditError(resp.data?.error || "Failed to save");
      }
    } catch (e) {
      setEditError(e?.response?.data?.error || e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleExportCsv = (runId) => {
    if (!runId) return;
    const url = `/api/broker/proposal-runs/${runId}/csv`;
    window.open(url, "_blank");
  };

  const renderConnectionCard = () => {
    if (!status) {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <CircularProgress size={20} /> <span>Checking broker connection...</span>
          </CardContent>
        </Card>
      );
    }
    const configured = !!status.configured;
    const reachable = !!status.reachable;
    let chipColor = "default";
    let label = "Unknown";
    if (!configured) {
      chipColor = "warning";
      label = "Not configured";
    } else if (reachable) {
      chipColor = "success";
      label = "Connected";
    } else {
      chipColor = "error";
      label = "Unreachable";
    }
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" color="secondary">
                Alpaca Paper Account
              </Typography>
              <Chip label={label} color={chipColor} size="small" />
              {status.paper && <Chip label="PAPER" size="small" variant="outlined" />}
            </Box>
            <Button startIcon={<RefreshIcon />} onClick={handleRefresh} size="small">
              Refresh
            </Button>
          </Box>
          {status.account_number && (
            <Typography variant="caption" color="textSecondary">
              Account #{status.account_number} · {status.base_url}
            </Typography>
          )}
          {status.error && (
            <Alert severity={configured ? "error" : "info"} sx={{ mt: 2 }}>
              {status.error}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSetupGuide = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          How to connect Alpaca (paper trading)
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ paddingLeft: 20, marginTop: 8 }}>
            <li>
              Create a free account at{" "}
              <a href="https://alpaca.markets" target="_blank" rel="noreferrer">
                alpaca.markets
              </a>{" "}
              and switch to <strong>Paper Trading</strong>.
            </li>
            <li>
              In your Alpaca dashboard generate an API key/secret pair for the
              paper environment.
            </li>
            <li>
              Add the following secrets to this app:
              <ul>
                <li>
                  <code>ALPACA_API_KEY</code>
                </li>
                <li>
                  <code>ALPACA_API_SECRET</code>
                </li>
                <li>
                  <code>ALPACA_BASE_URL</code> (optional, defaults to{" "}
                  <code>https://paper-api.alpaca.markets</code>)
                </li>
              </ul>
            </li>
            <li>Restart the backend, then refresh this tab.</li>
          </ol>
        </Typography>
      </CardContent>
    </Card>
  );

  const renderAccountCards = () => {
    if (!account) return null;
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="textSecondary">Cash</Typography>
              <Typography variant="h5">{fmtUsd(account.cash)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="textSecondary">Buying Power</Typography>
              <Typography variant="h5">{fmtUsd(account.buying_power)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="textSecondary">Equity</Typography>
              <Typography variant="h5" color="primary">{fmtUsd(account.equity)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const orderStatusColor = (st) => {
    const s = (st || "").toLowerCase();
    if (s === "filled") return "success";
    if (["new", "accepted", "pending_new", "partially_filled", "accepted_for_bidding"].includes(s)) return "info";
    if (["canceled", "expired", "replaced", "done_for_day"].includes(s)) return "default";
    if (["rejected", "suspended", "stopped"].includes(s)) return "error";
    return "default";
  };

  const renderOrders = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" color="secondary">
            Broker Orders ({orders.length})
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="orders-status-label">Status</InputLabel>
              <Select
                labelId="orders-status-label"
                label="Status"
                value={ordersStatus}
                onChange={(e) => {
                  setOrdersStatus(e.target.value);
                  loadOrders(e.target.value);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadOrders(ordersStatus)}
              disabled={ordersLoading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        {ordersError && <Alert severity="error" sx={{ mb: 1 }}>{ordersError}</Alert>}
        {ordersLoading ? (
          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={20} /></Box>
        ) : orders.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No {ordersStatus === "all" ? "" : ordersStatus + " "}orders.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Filled</TableCell>
                  <TableCell align="right">Avg Fill</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>TIF</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Client ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.broker_order_id || o.client_order_id}>
                    <TableCell>
                      <Typography variant="caption">
                        {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell><strong>{o.ticker}</strong></TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{o.side}</TableCell>
                    <TableCell align="right">{fmtNum(o.qty, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(o.filled_qty || 0, 0)}</TableCell>
                    <TableCell align="right">
                      {o.filled_avg_price ? fmtUsd(o.filled_avg_price) : "—"}
                    </TableCell>
                    <TableCell>{o.type}</TableCell>
                    <TableCell>{o.time_in_force}</TableCell>
                    <TableCell>
                      <Chip
                        label={o.status}
                        size="small"
                        color={orderStatusColor(o.status)}
                        variant={orderStatusColor(o.status) === "default" ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {o.client_order_id || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderPositions = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="secondary">
          Current Positions ({positions.length})
        </Typography>
        {positions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No open positions.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Avg Entry</TableCell>
                  <TableCell align="right">Mkt Price</TableCell>
                  <TableCell align="right">Mkt Value</TableCell>
                  <TableCell align="right">Unrealized P/L</TableCell>
                  <TableCell align="right">P/L %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((p) => (
                  <TableRow key={p.ticker}>
                    <TableCell><strong>{p.ticker}</strong></TableCell>
                    <TableCell align="right">{fmtNum(p.qty, 2)}</TableCell>
                    <TableCell align="right">{fmtUsd(p.avg_entry_price)}</TableCell>
                    <TableCell align="right">{fmtUsd(p.market_price)}</TableCell>
                    <TableCell align="right">{fmtUsd(p.market_value)}</TableCell>
                    <TableCell align="right" sx={{ color: p.unrealized_pl >= 0 ? "success.main" : "error.main" }}>
                      {fmtUsd(p.unrealized_pl)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: p.unrealized_plpc >= 0 ? "success.main" : "error.main" }}>
                      {fmtPct(p.unrealized_plpc)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderProposalsControls = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="secondary">
          Proposed Trades
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceType}
                label="Source"
                onChange={(e) => setSourceType(e.target.value)}
              >
                <MenuItem value="watchlist">Latest analysis per ticker in watchlist</MenuItem>
                <MenuItem value="ticker">Latest analysis by ticker(s)</MenuItem>
                <MenuItem value="ids">Specific analysis IDs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {sourceType === "ticker" && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Ticker(s) — comma/space separated"
                value={tickersInput}
                onChange={(e) => setTickersInput(e.target.value)}
                placeholder="AAPL TSLA NVDA"
              />
            </Grid>
          )}
          {sourceType === "watchlist" && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Watchlist</InputLabel>
                <Select
                  value={watchlistId}
                  label="Watchlist"
                  onChange={(e) => setWatchlistId(e.target.value)}
                >
                  <MenuItem value=""><em>Select a watchlist…</em></MenuItem>
                  {watchlists.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name} ({w.item_count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {sourceType === "ids" && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Analysis IDs (comma/space separated)"
                value={analysisIdsInput}
                onChange={(e) => setAnalysisIdsInput(e.target.value)}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sizing Mode</InputLabel>
              <Select value={mode} label="Sizing Mode" onChange={(e) => setMode(e.target.value)}>
                <MenuItem value="equal_weight">Equal Weight</MenuItem>
                <MenuItem value="confidence_weighted">Confidence Weighted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Position Size (0-1)"
              value={maxPosSize}
              onChange={(e) => setMaxPosSize(e.target.value)}
              inputProps={{ step: 0.05, min: 0.01, max: 1 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Gross Target (0-2)"
              value={grossTarget}
              onChange={(e) => setGrossTarget(e.target.value)}
              inputProps={{ step: 0.1, min: 0.1, max: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Label (optional)"
              value={runLabel}
              onChange={(e) => setRunLabel(e.target.value)}
              placeholder="e.g. Morning rebalance"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleComputeProposals}
              disabled={proposalsLoading}
            >
              {proposalsLoading ? "Computing…" : "Compute Proposals"}
            </Button>
          </Grid>
        </Grid>

        {proposalsError && <Alert severity="error" sx={{ mt: 2 }}>{proposalsError}</Alert>}

        {proposalsResp && (
          <Box mt={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {proposalsResp.run_id ? (
                    <>Run <strong>#{proposalsResp.run_id}</strong>
                    {proposalsResp.label ? <> · <strong>{proposalsResp.label}</strong></> : null}
                    {proposalsResp.created_at ? <> · {new Date(proposalsResp.created_at).toLocaleString()}</> : null}
                    {proposalsResp.historical ? <> · <em>(historical)</em></> : null}
                    {" · "}</>
                  ) : null}
                  Mode: <strong>{proposalsResp.mode}</strong> · Equity used:{" "}
                  <strong>{fmtUsd(proposalsResp.equity)}</strong> · Max position:{" "}
                  <strong>{fmtPct(proposalsResp.max_position_size)}</strong> · Gross target:{" "}
                  <strong>{fmtPct(proposalsResp.gross_target)}</strong>
                </Typography>
                {proposalsResp.run_id && proposalsResp.notes && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", fontStyle: "italic", mt: 0.5 }}
                  >
                    Notes: {proposalsResp.notes}
                  </Typography>
                )}
              </Box>
              {proposalsResp.run_id && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportCsv(proposalsResp.run_id)}
                >
                  Export CSV
                </Button>
              )}
            </Box>
            {(proposalsResp.warnings || []).map((w, i) => (
              <Alert key={i} severity="warning" sx={{ mt: 1 }}>{w}</Alert>
            ))}
            {proposalsResp.run_id && renderExecutionToolbar()}
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {proposalsResp.run_id && (
                      <TableCell padding="checkbox">
                        <Tooltip title="Select all actionable">
                          <Checkbox
                            size="small"
                            indeterminate={
                              selected.size > 0 &&
                              selected.size < proposalsResp.proposals.filter(isActionable).length
                            }
                            checked={
                              proposalsResp.proposals.filter(isActionable).length > 0 &&
                              selected.size === proposalsResp.proposals.filter(isActionable).length
                            }
                            onChange={(e) =>
                              e.target.checked ? selectAllActionable() : clearSelection()
                            }
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell>Ticker</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell align="right">Confidence</TableCell>
                    <TableCell align="right">Target Wt</TableCell>
                    <TableCell align="right">Target $</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Target Sh</TableCell>
                    <TableCell align="right">Current Sh</TableCell>
                    <TableCell align="right">Δ Shares</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposalsResp.proposals.map((p, i) => (
                    <TableRow
                      key={i}
                      selected={selected.has(i)}
                      hover={isActionable(p)}
                    >
                      {proposalsResp.run_id && (
                        <TableCell padding="checkbox">
                          {isActionable(p) ? (
                            <Checkbox
                              size="small"
                              checked={selected.has(i)}
                              onChange={() => toggleSelected(i)}
                            />
                          ) : (
                            <Tooltip title={p.action === "HOLD" ? "Hold — no action" : "Not actionable"}>
                              <span style={{ display: "inline-block", width: 24 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell><strong>{p.ticker}</strong></TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={p.decision_normalized || p.decision || "—"}
                          color={decisionColor(p.decision_normalized || p.decision)}
                        />
                      </TableCell>
                      <TableCell align="right">{p.confidence != null ? fmtPct(p.confidence) : "—"}</TableCell>
                      <TableCell align="right">{fmtPct(p.target_weight)}</TableCell>
                      <TableCell align="right">{fmtUsd(p.target_dollars)}</TableCell>
                      <TableCell align="right">{p.price != null ? fmtUsd(p.price) : "—"}</TableCell>
                      <TableCell align="right">{fmtNum(p.target_shares, 2)}</TableCell>
                      <TableCell align="right">{fmtNum(p.current_shares, 2)}</TableCell>
                      <TableCell align="right">{fmtNum(p.delta_shares, 2)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.action} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {p.source?.ticker} · {p.source?.decision || "—"}
                          {p.source?.model ? ` · ${p.source.model}` : ""}
                          {p.source?.created_at ? ` · ${p.source.created_at.slice(0, 10)}` : ""}
                          {p.source?.analysis_id ? ` · #${p.source.analysis_id}` : ""}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {proposalsResp.proposals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={proposalsResp.run_id ? 12 : 11}>
                        <Typography variant="body2" color="textSecondary" align="center">
                          No proposals to show.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {proposalsResp.run_id && renderExecutionsPanel()}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderExecutionToolbar = () => {
    const total = proposalsResp.proposals.filter(isActionable).length;
    return (
      <Box
        sx={{
          mt: 2, p: 1.5, borderRadius: 1,
          border: 1, borderColor: "divider",
          bgcolor: "action.hover",
          display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
        }}
      >
        <Typography variant="body2">
          <strong>{selected.size}</strong> of {total} actionable selected
          {selectionSummary.count > 0 && (
            <>
              {" · "}{selectionSummary.buys} buy / {selectionSummary.sells} sell
              {" · "}gross {fmtUsd(selectionSummary.gross)}
              {" · "}net {fmtUsd(selectionSummary.net)}
            </>
          )}
        </Typography>
        <Box flex={1} />
        <Button size="small" onClick={selectAllActionable} disabled={total === 0}>
          Select all
        </Button>
        <Button size="small" onClick={clearSelection} disabled={selected.size === 0}>
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          color="warning"
          startIcon={<PlayArrowIcon />}
          onClick={openExecuteDialog}
          disabled={selected.size === 0}
        >
          Execute paper trades…
        </Button>
      </Box>
    );
  };

  const renderExecutionsPanel = () => (
    <Box mt={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle1">
          Executed orders for run #{proposalsResp.run_id} ({executions.length})
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => loadExecutions(proposalsResp.run_id)}
          disabled={executionsLoading}
        >
          Refresh
        </Button>
      </Box>
      {executions.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No execution attempts yet for this run.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Ticker</TableCell>
                <TableCell>Side</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Filled</TableCell>
                <TableCell align="right">Avg Px</TableCell>
                <TableCell align="right">Est $</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Broker ID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>{o.id}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell><strong>{o.ticker}</strong></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={o.side?.toUpperCase()}
                      color={o.side === "buy" ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell align="right">{fmtNum(o.qty, 2)}</TableCell>
                  <TableCell align="right">{o.filled_qty != null ? fmtNum(o.filled_qty, 2) : "—"}</TableCell>
                  <TableCell align="right">{o.filled_avg_price != null ? fmtUsd(o.filled_avg_price) : "—"}</TableCell>
                  <TableCell align="right">{o.est_notional != null ? fmtUsd(o.est_notional) : "—"}</TableCell>
                  <TableCell>
                    <Tooltip title={o.error || ""}>
                      <Chip
                        size="small"
                        label={o.status || "—"}
                        color={
                          o.status === "filled" ? "success"
                          : o.status === "error" || o.status === "rejected" ? "error"
                          : o.status === "dry_run" ? "default" : "info"
                        }
                        variant={o.status === "dry_run" ? "outlined" : "filled"}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {o.dry_run ? (
                      <Chip size="small" label="dry-run" variant="outlined" />
                    ) : (
                      <Chip size="small" label="LIVE" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {o.broker_order_id ? o.broker_order_id.slice(0, 8) + "…" : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {!o.dry_run && o.broker_order_id && (
                      <Tooltip title="Refresh status from broker">
                        <IconButton size="small" onClick={() => refreshOrder(o.id)}>
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderHistoryPanel = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon fontSize="small" />
            <Typography variant="h6" color="secondary">
              Saved Proposal Runs ({history.length})
            </Typography>
          </Box>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadHistory}>
            Refresh
          </Button>
        </Box>
        {historyError && <Alert severity="error" sx={{ mb: 1 }}>{historyError}</Alert>}
        {historyLoading ? (
          <Box><CircularProgress size={20} /></Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No saved runs yet. Compute proposals to save your first run.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Max Pos</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Equity</TableCell>
                  <TableCell align="right">Trades</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow
                    key={h.id}
                    hover
                    selected={activeRunId === h.id}
                  >
                    <TableCell>{h.id}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {h.created_at ? new Date(h.created_at).toLocaleString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {h.label || <span style={{ opacity: 0.5 }}>—</span>}
                        {h.notes && (
                          <Tooltip title={h.notes}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              sx={{
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontStyle: "italic",
                              }}
                            >
                              {h.notes}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{h.source_type || "—"}</TableCell>
                    <TableCell>{h.mode}</TableCell>
                    <TableCell align="right">{fmtPct(h.max_position_size)}</TableCell>
                    <TableCell align="right">{fmtPct(h.gross_target)}</TableCell>
                    <TableCell align="right">{fmtUsd(h.equity)}</TableCell>
                    <TableCell align="right">{h.proposal_count}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => handleOpenRun(h.id)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rename / annotate">
                        <IconButton size="small" onClick={() => handleOpenEdit(h)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export CSV">
                        <IconButton size="small" onClick={() => handleExportCsv(h.id)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteRun(h.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const configured = status?.configured && status?.reachable;

  return (
    <Box>
      <Alert
        severity="info"
        icon={<WarningAmberIcon />}
        sx={{ mb: 2, fontWeight: "bold" }}
      >
        Preview only — no orders placed. This tab never submits trades.
      </Alert>

      {renderConnectionCard()}

      {!status?.configured && renderSetupGuide()}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {configured && (
        <>
          {loading && <Box mb={2}><CircularProgress size={20} /></Box>}
          {renderAccountCards()}
          {renderOrders()}
          {renderPositions()}
        </>
      )}

      <Divider sx={{ my: 2 }} />
      {renderProposalsControls()}
      {renderHistoryPanel()}

      <Dialog
        open={execDialogOpen}
        onClose={closeExecuteDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {execDryRun ? "Dry-run paper trades" : "Execute paper trades"}
          {proposalsResp?.run_id ? ` · Run #${proposalsResp.run_id}` : ""}
        </DialogTitle>
        <DialogContent>
          <Alert severity={execDryRun ? "info" : "warning"} sx={{ mb: 2 }}>
            {execDryRun
              ? "Dry run only: no orders will be sent to the broker. Each attempt is logged below for review."
              : "Live submission to the Alpaca paper account. Orders are idempotent per ticker per run — re-clicking will not duplicate them."}
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{selectionSummary.count}</strong> orders ·{" "}
              {selectionSummary.buys} buy / {selectionSummary.sells} sell ·{" "}
              gross <strong>{fmtUsd(selectionSummary.gross)}</strong> ·{" "}
              net <strong>{fmtUsd(selectionSummary.net)}</strong>
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Qty (int)</TableCell>
                  <TableCell align="right">~Price</TableCell>
                  <TableCell align="right">~Notional</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(selected).sort((a, b) => a - b).map((i) => {
                  const p = proposalsResp.proposals[i];
                  if (!p) return null;
                  const qty = Math.floor(Math.abs(Number(p.delta_shares)));
                  const side = Number(p.delta_shares) > 0 ? "buy" : "sell";
                  return (
                    <TableRow key={i}>
                      <TableCell><strong>{p.ticker}</strong></TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={side.toUpperCase()}
                          color={side === "buy" ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="right">{qty}</TableCell>
                      <TableCell align="right">{fmtUsd(p.price)}</TableCell>
                      <TableCell align="right">{fmtUsd(qty * Number(p.price))}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small"
                label="Max $ per order"
                type="number"
                value={execMaxPerOrder}
                onChange={(e) => setExecMaxPerOrder(e.target.value)}
                disabled={execSubmitting}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small"
                label="Max total $"
                type="number"
                value={execMaxTotal}
                onChange={(e) => setExecMaxTotal(e.target.value)}
                disabled={execSubmitting}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                checked={execDryRun}
                onChange={(e) => {
                  setExecDryRun(e.target.checked);
                  setExecConfirmInput("");
                  setExecError(null);
                }}
                disabled={execSubmitting}
              />
            }
            label="Dry run (recommended first)"
          />

          {!execDryRun && (
            <TextField
              fullWidth size="small"
              sx={{ mt: 2 }}
              label={`Type the run id (${proposalsResp?.run_id ?? ""}) to confirm`}
              value={execConfirmInput}
              onChange={(e) => setExecConfirmInput(e.target.value)}
              disabled={execSubmitting}
              autoFocus
            />
          )}

          {execError && <Alert severity="error" sx={{ mt: 2 }}>{execError}</Alert>}

          {execResult && (
            <Box mt={2}>
              {(() => {
                // Surface selected intents that were filtered out before submission.
                const skippedSelected = (execResult.intents || []).filter(
                  (it) => it.skipped_reason && it.skipped_reason !== "not_selected"
                            && it.skipped_reason !== "skipped_by_proposal",
                );
                const submittedCount = execResult.submitted?.length || 0;
                if (submittedCount === 0 && skippedSelected.length > 0) {
                  return (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <strong>Nothing submitted.</strong> All {skippedSelected.length} selected
                      order(s) were filtered out before reaching the broker. See the reasons below
                      and adjust your caps or selection.
                    </Alert>
                  );
                }
                if (skippedSelected.length > 0) {
                  return (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {skippedSelected.length} of your selected order(s) were skipped before
                      submission. See "Skipped" rows below.
                    </Alert>
                  );
                }
                return null;
              })()}
              <Alert severity={execResult.dry_run ? "info" : "success"} sx={{ mb: 1 }}>
                {execResult.dry_run
                  ? `Dry-run complete: simulated ${execResult.submitted?.length || 0} order(s) (no broker contact).`
                  : `Submitted ${execResult.submitted?.length || 0} order(s) to broker. Initial status reflects the broker's immediate response — orders may stay in 'accepted'/'new' until market open or fill. Click the refresh icon on each order to re-poll status.`}
                {execResult.skipped_existing?.length > 0 && (
                  <> · {execResult.skipped_existing.length} skipped (already submitted earlier)</>
                )}
              </Alert>

              {(() => {
                const skipped = (execResult.intents || []).filter(
                  (it) => it.skipped_reason && it.skipped_reason !== "not_selected"
                            && it.skipped_reason !== "skipped_by_proposal",
                );
                if (skipped.length === 0) return null;
                return (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="caption" color="warning.main">
                              <strong>Skipped (selected but not submitted)</strong>
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Ticker</TableCell>
                          <TableCell>Side</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skipped.map((it, idx) => (
                          <TableRow key={idx}>
                            <TableCell><strong>{it.ticker}</strong></TableCell>
                            <TableCell>{it.side}</TableCell>
                            <TableCell align="right">{it.qty}</TableCell>
                            <TableCell>
                              <Typography variant="caption">{it.note || it.skipped_reason}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticker</TableCell>
                      <TableCell>Side</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Broker ID</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(execResult.submitted || []).map((s, idx) => (
                      <TableRow key={idx}>
                        <TableCell><strong>{s.intent.ticker}</strong></TableCell>
                        <TableCell>{s.intent.side}</TableCell>
                        <TableCell align="right">{s.intent.qty}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.status}
                            color={
                              s.status === "filled" ? "success"
                              : s.status === "error" || s.status === "rejected" ? "error"
                              : "default"
                            }
                            variant={s.status === "dry_run" ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {s.broker_order_id ? s.broker_order_id.slice(0, 8) + "…" : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {s.error && (
                            <Typography variant="caption" color="error">{s.error}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExecuteDialog} disabled={execSubmitting}>
            {execResult ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={submitExecution}
            variant="contained"
            color={execDryRun ? "primary" : "warning"}
            startIcon={<PlayArrowIcon />}
            disabled={
              execSubmitting ||
              selected.size === 0 ||
              (!execDryRun &&
                String(execConfirmInput).trim() !== String(proposalsResp?.run_id ?? ""))
            }
          >
            {execSubmitting ? "Submitting…" : execDryRun ? "Run dry-run" : "Submit live"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRun} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editRun ? `Edit run #${editRun.id}` : "Edit run"}
        </DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField
            label="Label"
            fullWidth
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            placeholder="e.g. Tech rebalance, Apr 20"
            inputProps={{ maxLength: 200 }}
            sx={{ mt: 1, mb: 2 }}
            autoFocus
            disabled={editSaving}
          />
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            placeholder="Why this run, what you're testing, follow-ups…"
            inputProps={{ maxLength: 5000 }}
            disabled={editSaving}
          />
          <Typography variant="caption" color="textSecondary">
            {editNotes.length}/5000
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={editSaving}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={editSaving}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TradingTab;
